import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useWorkspace } from '../lib/WorkspaceContext'
import { aISO, combinarItemsTracking } from '../lib/tracking'
import './Habitos.css'

const LETRAS_DIA = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do']
const NOMBRE_MES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
const ETIQUETA_CAMPO = { comidas: 'Comidas del día', horas_sueno: 'Horas de sueño', litros_agua: 'Litros de agua' }

function truncarNombre(texto, max = 50) {
  if (!texto || texto.length <= max) return texto
  return texto.slice(0, max - 1).trimEnd() + '…'
}

function lunesDeSemana(fechaISO) {
  const d = new Date(fechaISO + 'T00:00:00')
  const dow = d.getDay()
  const diff = dow === 0 ? -6 : 1 - dow
  d.setDate(d.getDate() + diff)
  return aISO(d)
}

function sumarDias(fechaISO, n) {
  const d = new Date(fechaISO + 'T00:00:00')
  d.setDate(d.getDate() + n)
  return aISO(d)
}

function diasDelMes(anio, mes) {
  return new Date(anio, mes + 1, 0).getDate()
}

export default function Habitos({ usuario }) {
  const { workspaceId } = useWorkspace()

  const [loading, setLoading] = useState(true)
  const [todasConfigs, setTodasConfigs] = useState([])
  const [catalogo, setCatalogo] = useState([])

  const [vista, setVista] = useState('semana')
  const hoyDate = new Date()
  const hoy = aISO(new Date(hoyDate.getFullYear(), hoyDate.getMonth(), hoyDate.getDate()))
  const [lunesSemana, setLunesSemana] = useState(() => lunesDeSemana(hoy))
  const [anio, setAnio] = useState(hoyDate.getFullYear())
  const [mes, setMes] = useState(hoyDate.getMonth())

  const [controlDiario, setControlDiario] = useState({})
  const [checksHabitos, setChecksHabitos] = useState({})
  const [suplementosActivos, setSuplementosActivos] = useState([])
  const [checksSuplementos, setChecksSuplementos] = useState({})

  const [modalActivar, setModalActivar] = useState(false)
  const [catalogoSeleccionado, setCatalogoSeleccionado] = useState(null)
  const [metaInput, setMetaInput] = useState('')
  const [guardando, setGuardando] = useState(false)

  useEffect(() => { if (workspaceId) cargarConfig() }, [workspaceId])

  const habitosActivos = todasConfigs.filter(c => c.activo)

  useEffect(() => {
    if (habitosActivos.length === 0 && suplementosActivos.length === 0) return
    const [inicio, fin] = vista === 'semana'
      ? [lunesSemana, sumarDias(lunesSemana, 6)]
      : [`${anio}-${String(mes + 1).padStart(2, '0')}-01`, `${anio}-${String(mes + 1).padStart(2, '0')}-${String(diasDelMes(anio, mes)).padStart(2, '0')}`]
    cargarDatosRango(inicio, fin)
  }, [vista, lunesSemana, anio, mes, todasConfigs.length, suplementosActivos.length])

  const cargarConfig = async () => {
    setLoading(true)
    const [{ data: configs }, { data: cat }, { data: suplementos }] = await Promise.all([
      supabase.from('habitos_config')
        .select('id, meta_valor, semana_inicio, activo, habitos_catalogo(id, nombre, tipo, campo_referencia)')
        .eq('paciente_id', usuario.id),
      supabase.from('habitos_catalogo').select('*').eq('workspace_id', workspaceId).eq('activo', true),
      supabase.from('suplementos_config')
        .select('id, created_at, suplementos_catalogo(nombre)')
        .eq('paciente_id', usuario.id).eq('activo_paciente', true)
    ])
    setTodasConfigs(configs || [])
    setCatalogo(cat || [])
    setSuplementosActivos(suplementos || [])
    setLoading(false)
  }

  const cargarDatosRango = async (inicio, fin) => {
    const activos = todasConfigs.filter(c => c.activo)
    const tieneBaseDiario = activos.some(c => c.habitos_catalogo.tipo === 'base_diario')
    const idsPersonalizados = activos.filter(c => c.habitos_catalogo.tipo === 'personalizado').map(c => c.id)
    const idsSuplementos = suplementosActivos.map(s => s.id)

    const tareas = []
    if (tieneBaseDiario) {
      tareas.push(
        supabase.from('control_diario')
          .select('fecha, horas_sueno, litros_agua, registro_comidas(tipo_comida, descripcion, realizada)')
          .eq('usuario_id', usuario.id)
          .gte('fecha', inicio).lte('fecha', fin)
          .then(({ data }) => {
            setControlDiario(prev => {
              const next = { ...prev }
              for (const fila of data ?? []) next[fila.fecha] = fila
              return next
            })
          })
      )
    }
    if (idsPersonalizados.length > 0) {
      tareas.push(
        supabase.from('registro_habito_personalizado')
          .select('habitos_config_id, fecha, cumplido')
          .in('habitos_config_id', idsPersonalizados)
          .gte('fecha', inicio).lte('fecha', fin)
          .then(({ data }) => {
            setChecksHabitos(prev => {
              const next = { ...prev }
              for (const c of data ?? []) next[`${c.habitos_config_id}:${c.fecha}`] = c.cumplido
              return next
            })
          })
      )
    }
    if (idsSuplementos.length > 0) {
      tareas.push(
        supabase.from('registro_suplemento')
          .select('suplementos_config_id, fecha, cumplido')
          .in('suplementos_config_id', idsSuplementos)
          .gte('fecha', inicio).lte('fecha', fin)
          .then(({ data }) => {
            setChecksSuplementos(prev => {
              const next = { ...prev }
              for (const c of data ?? []) next[`${c.suplementos_config_id}:${c.fecha}`] = c.cumplido
              return next
            })
          })
      )
    }
    await Promise.all(tareas)
  }

  const abrirModalActivar = (catalogoItem) => {
    setCatalogoSeleccionado(catalogoItem)
    setMetaInput('')
    setModalActivar(true)
  }

  const activarHabito = async () => {
    if (!catalogoSeleccionado) return
    setGuardando(true)
    const existente = todasConfigs.find(c => c.habitos_catalogo.id === catalogoSeleccionado.id)
    const metaValor = metaInput.trim() ? parseFloat(metaInput) : null
    const hoyStr = new Date().toISOString().slice(0, 10)
    let error
    if (existente) {
      ({ error } = await supabase.from('habitos_config')
        .update({ activo: true, meta_valor: metaValor, semana_inicio: hoyStr })
        .eq('id', existente.id))
    } else {
      ({ error } = await supabase.from('habitos_config').insert({
        paciente_id: usuario.id,
        habito_catalogo_id: catalogoSeleccionado.id,
        meta_valor: metaValor,
        semana_inicio: hoyStr
      }))
    }
    setGuardando(false)
    if (error) { alert('No se pudo activar el hábito.'); return }
    setModalActivar(false)
    setCatalogoSeleccionado(null)
    cargarConfig()
  }

  const desactivarHabito = async (configId) => {
    await supabase.from('habitos_config').update({ activo: false }).eq('id', configId)
    setTodasConfigs(prev => prev.map(c => c.id === configId ? { ...c, activo: false } : c))
  }

  const toggleCheckItem = async (item, fecha) => {
    if (!item.tildable) return
    if (fecha > hoy || fecha < item.fechaDesde) return
    const clave = `${item.idRegistro}:${fecha}`
    if (item.tipo === 'habito_manual') {
      const actual = checksHabitos[clave] === true
      const nuevo = !actual
      setChecksHabitos(prev => ({ ...prev, [clave]: nuevo }))
      const { error } = await supabase.from('registro_habito_personalizado')
        .upsert({ habitos_config_id: item.idRegistro, fecha, cumplido: nuevo }, { onConflict: 'habitos_config_id,fecha' })
      if (error) setChecksHabitos(prev => ({ ...prev, [clave]: actual }))
    } else if (item.tipo === 'suplemento') {
      const actual = checksSuplementos[clave] === true
      const nuevo = !actual
      setChecksSuplementos(prev => ({ ...prev, [clave]: nuevo }))
      const { error } = await supabase.from('registro_suplemento')
        .upsert({ suplementos_config_id: item.idRegistro, fecha, cumplido: nuevo }, { onConflict: 'suplementos_config_id,fecha' })
      if (error) setChecksSuplementos(prev => ({ ...prev, [clave]: actual }))
    }
  }

  if (loading) return <div className="loading">Cargando hábitos...</div>

  const idsConfigurados = new Set(todasConfigs.filter(c => c.activo).map(c => c.habitos_catalogo.id))
  const catalogoDisponible = catalogo.filter(c => !idsConfigurados.has(c.id))

  const diasVisibles = vista === 'semana'
    ? LETRAS_DIA.map((letra, i) => ({ fecha: sumarDias(lunesSemana, i), etiqueta: `${letra} ${new Date(sumarDias(lunesSemana, i) + 'T00:00:00').getDate()}` }))
    : Array.from({ length: diasDelMes(anio, mes) }, (_, i) => {
        const f = `${anio}-${String(mes + 1).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`
        return { fecha: f, etiqueta: String(i + 1) }
      })

  const esSemanaActual = lunesSemana === lunesDeSemana(hoy)
  const esMesActual = anio === hoyDate.getFullYear() && mes === hoyDate.getMonth()

  const itemsTracking = combinarItemsTracking(habitosActivos, suplementosActivos, controlDiario, checksHabitos, checksSuplementos, desactivarHabito)

  const totalesPorDia = diasVisibles.map(d => {
    const activosEnRango = itemsTracking.filter(item => d.fecha >= item.fechaDesde)
    const cumplidos = activosEnRango.filter(item => item.cumplido(d.fecha)).length
    return { fecha: d.fecha, cumplidos, total: activosEnRango.length }
  })

  return (
    <div className="habitos-page">
      <div className="page-header">
        <h1 className="page-titulo">Hábitos</h1>
        <p className="page-subtitulo">Tu seguimiento diario</p>
      </div>

      <div className="habitos-contenido">
        <div className="habitos-vista-toggle">
          <button className={`habitos-vista-btn ${vista === 'semana' ? 'active' : ''}`} onClick={() => setVista('semana')}>Semana</button>
          <button className={`habitos-vista-btn ${vista === 'mes' ? 'active' : ''}`} onClick={() => setVista('mes')}>Mes</button>
        </div>

        {vista === 'semana' ? (
          <div className="diario-tira-semanal">
            <button className="diario-tira-flecha" onClick={() => setLunesSemana(sumarDias(lunesSemana, -7))}>‹</button>
            <p style={{ flex: 1, textAlign: 'center', fontSize: 13, fontWeight: 600 }}>
              Semana del {new Date(lunesSemana + 'T00:00:00').getDate()}/{new Date(lunesSemana + 'T00:00:00').getMonth() + 1}
            </p>
            <button
              className="diario-tira-flecha"
              onClick={() => setLunesSemana(sumarDias(lunesSemana, 7))}
              disabled={esSemanaActual}
              style={{ opacity: esSemanaActual ? 0.3 : 1 }}
            >›</button>
          </div>
        ) : (
          <div className="diario-tira-semanal">
            <button className="diario-tira-flecha" onClick={() => { if (mes === 0) { setMes(11); setAnio(a => a - 1) } else setMes(m => m - 1) }}>‹</button>
            <p style={{ flex: 1, textAlign: 'center', fontSize: 13, fontWeight: 600 }}>{NOMBRE_MES[mes]} {anio}</p>
            <button
              className="diario-tira-flecha"
              onClick={() => { if (mes === 11) { setMes(0); setAnio(a => a + 1) } else setMes(m => m + 1) }}
              disabled={esMesActual}
              style={{ opacity: esMesActual ? 0.3 : 1 }}
            >›</button>
          </div>
        )}

        {itemsTracking.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🏃</div>
            <p>Todavía no activaste ningún hábito.</p>
          </div>
        ) : (
          <div className="habitos-tabla-wrap">
            <table className="habitos-tabla">
              <thead>
                <tr>
                  <th>Hábito</th>
                  {diasVisibles.map(d => <th key={d.fecha}>{d.etiqueta}</th>)}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {itemsTracking.map(item => {
                  const diasEnRango = diasVisibles.filter(d => d.fecha >= item.fechaDesde)
                  const cumplidos = diasEnRango.filter(d => d.fecha <= hoy && item.cumplido(d.fecha)).length
                  const pasados = diasEnRango.filter(d => d.fecha <= hoy).length
                  return (
                    <tr key={item.id}>
                      <td>
                        <span className="habitos-tabla-nombre" title={item.nombre}>
                          {truncarNombre(item.nombre)}
                        </span>
                        {item.onDesactivar && (
                          <button className="habitos-btn-desactivar" onClick={item.onDesactivar} title="Desactivar hábito">✕</button>
                        )}
                      </td>
                      {diasVisibles.map(d => {
                        const antesDeInicio = d.fecha < item.fechaDesde
                        const esFuturo = d.fecha > hoy
                        if (antesDeInicio) return <td key={d.fecha} style={{ textAlign: 'center', opacity: 0.3 }}>—</td>
                        if (esFuturo) return <td key={d.fecha} style={{ textAlign: 'center', opacity: 0.3 }}>—</td>
                        const cumplido = item.cumplido(d.fecha)
                        return (
                          <td key={d.fecha} style={{ textAlign: 'center' }}>
                            {item.tildable ? (
                              <button
                                className={`habitos-check ${cumplido ? 'cumplido' : ''}`}
                                onClick={() => toggleCheckItem(item, d.fecha)}
                              >{cumplido ? '✓' : ''}</button>
                            ) : (
                              <span>{cumplido ? '✓' : '·'}</span>
                            )}
                          </td>
                        )
                      })}
                      <td>{cumplidos}/{pasados}</td>
                    </tr>
                  )
                })}
                <tr className="habitos-fila-total">
                  <td>Total del día</td>
                  {totalesPorDia.map(t => (
                    <td key={t.fecha} style={{ textAlign: 'center' }}>{t.total > 0 ? `${t.cumplidos}/${t.total}` : '—'}</td>
                  ))}
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="habitos-seccion-activar">
          <p className="seccion-titulo">+ Activar un hábito</p>
          {catalogoDisponible.length === 0 ? (
            <p className="admin-ws-vacio">No hay más hábitos disponibles para activar.</p>
          ) : (
            <div className="habitos-catalogo-lista">
              {catalogoDisponible.map(c => (
                <div key={c.id} className="habitos-catalogo-item">
                  <div>
                    <p className="admin-ws-item-nombre">{c.nombre}</p>
                    <p className="admin-ws-item-detalle">
                      {c.tipo === 'base_diario' ? `Automático · se completa solo con tu ${ETIQUETA_CAMPO[c.campo_referencia] || c.campo_referencia}` : 'Manual · lo tildás vos cada día'}
                    </p>
                  </div>
                  <button className="btn-secundario admin-ws-btn-chico" onClick={() => abrirModalActivar(c)}>Activar</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {modalActivar && (
        <div className="modal-overlay" onClick={() => setModalActivar(false)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h3 className="modal-titulo">Activar "{catalogoSeleccionado?.nombre}"</h3>
            {catalogoSeleccionado?.campo_referencia && catalogoSeleccionado.campo_referencia !== 'comidas' && (
              <>
                <label className="form-label">Meta ({ETIQUETA_CAMPO[catalogoSeleccionado.campo_referencia]}) — opcional</label>
                <input
                  className="form-input"
                  type="number"
                  step="0.1"
                  placeholder="Ej: 8"
                  value={metaInput}
                  onChange={e => setMetaInput(e.target.value)}
                />
              </>
            )}
            <div className="modal-actions">
              <button className="btn-secundario" onClick={() => setModalActivar(false)}>Cancelar</button>
              <button className="btn-primario" onClick={activarHabito} disabled={guardando}>
                {guardando ? 'Activando...' : 'Activar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
